/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { createLocalZustand } from '@/shared/state';
import type { LeadOrigin, LeadInteraction, ClientContact } from './ClientManagementContext';

/**
 * The stand-in when a client has no recorded origin.
 *
 * Lives beside the target type rather than in whichever component happened
 * to need it first: two callers now raise this panel — the profile section
 * and the phone's action bar — and a second copy of the literal is a second
 * chance to disagree about what "no origin" is.
 */
export const EMPTY_LEAD_ORIGIN: LeadOrigin = { platform: '', source: '' };

export interface SocialChatTarget {
  agencyId: string;
  clientId: string;
  clientName?: string;
  leadOrigin: LeadOrigin;
  leadInteractions?: LeadInteraction[];
  contacts?: ClientContact[];
  onMerged?: () => void;
  /** Preview/test-only escape hatch: skips the real fetchPortalConnection call when set. */
  portalConnectedOverride?: boolean;
}

interface SocialChatWidgetState {
  target: SocialChatTarget | null;
  minimized: boolean;
  openChat: (target: SocialChatTarget) => void;
  closeChat: () => void;
  toggleMinimize: () => void;
}

/**
 * Agency-scoped store backing the floating Social chat widget. Provider is
 * mounted once at AgencyDashboardLayout so `target` (and therefore the open
 * panel) survives navigation between agency pages instead of unmounting with
 * whichever client profile page opened it.
 */
export const socialChatWidgetContext = createLocalZustand<SocialChatWidgetState>(
  () => (set) => ({
    target: null,
    minimized: false,
    openChat: (target) => set({ target, minimized: false }),
    closeChat: () => set({ target: null, minimized: false }),
    toggleMinimize: () => set((state) => ({ minimized: !state.minimized })),
  }),
);

export const { useStore: useSocialChatWidget } = socialChatWidgetContext;
